import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Plus,
  Trash2,
  Save,
  GripVertical,
  ChevronDown,
  ChevronUp,
  FileText,
  Loader
} from 'lucide-react';
import { doc, setDoc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { DndContext, DragEndEvent, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Rule {
  id: string;
  text: string;
  order: number;
  createdAt: Timestamp;
  expanded?: boolean;
}

interface GeneralRulesModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  categories: Array<{ id: string; name: string }>;
}

// Componente para una regla individual con drag & drop
const SortableRule: React.FC<{
  rule: Rule;
  onUpdate: (text: string) => void;
  onDelete: () => void;
  onToggleExpand: () => void;
  categories: Array<{ id: string; name: string }>;
}> = ({ rule, onUpdate, onDelete, onToggleExpand, categories }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(rule.text);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: rule.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Renderizar texto con @menciones como pills
  const renderTextWithMentions = (text: string) => {
    // Crear un mapa de categorías para búsqueda rápida
    const categoryMap = new Map(categories.map(c => [c.name.toLowerCase(), c.name]));
    
    // Regex mejorado que captura @palabra y opcionalmente más palabras después
    // Captura desde @ hasta que encuentra: otro @, fin de línea, o puntuación seguida de espacio
    const parts: Array<{ type: 'text' | 'mention'; content: string }> = [];
    let lastIndex = 0;
    
    // Buscar todas las posibles menciones que empiezan con @
    const mentionRegex = /@([^\s@][^@,.\n]*)/g;
    let match;
    
    while ((match = mentionRegex.exec(text)) !== null) {
      // Agregar el texto antes de la mención
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.substring(lastIndex, match.index)
        });
      }
      
      // El texto capturado después del @
      let capturedText = match[1].trim();
      
      // Buscar la categoría más larga que coincida desde el inicio
      let foundCategory: string | null = null;
      
      // Intentar encontrar coincidencias exactas primero
      if (categoryMap.has(capturedText.toLowerCase())) {
        foundCategory = categoryMap.get(capturedText.toLowerCase())!;
      } else {
        // Si no hay coincidencia exacta, buscar coincidencias parciales
        // desde la más larga hasta la más corta
        const sortedCategories = [...categories].sort((a, b) => b.name.length - a.name.length);
        
        for (const category of sortedCategories) {
          if (capturedText.toLowerCase().startsWith(category.name.toLowerCase())) {
            foundCategory = category.name;
            break;
          }
        }
      }
      
      if (foundCategory) {
        parts.push({
          type: 'mention',
          content: foundCategory
        });
        // Actualizar el índice para continuar después de la categoría encontrada
        lastIndex = match.index + 1 + foundCategory.length; // +1 por el @
        
        // Si hay texto adicional después de la categoría, agregarlo
        const remainingInMatch = capturedText.substring(foundCategory.length);
        if (remainingInMatch) {
          parts.push({
            type: 'text',
            content: remainingInMatch
          });
          lastIndex = match.index + match[0].length;
        }
      } else {
        // No es una categoría válida, mostrar como texto normal
        parts.push({
          type: 'text',
          content: match[0]
        });
        lastIndex = match.index + match[0].length;
      }
    }
    
    // Agregar cualquier texto restante
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex)
      });
    }
    
    // Renderizar las partes
    return parts.map((part, index) => {
      if (part.type === 'mention') {
        return (
          <span
            key={`mention-${index}`}
            className="inline-flex items-center px-2 py-0.5 mx-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium"
          >
            @{part.content}
          </span>
        );
      }
      return <span key={`text-${index}`}>{part.content}</span>;
    });
  };

  const handleSave = () => {
    onUpdate(editText);
    setIsEditing(false);
  };

  const isExpanded = rule.expanded !== false && (isEditing || rule.text.length <= 200);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border border-gray-200 rounded-lg bg-white ${isDragging ? 'shadow-lg' : ''}`}
    >
      <div className="flex items-start p-3">
        <div
          {...attributes}
          {...listeners}
          className="cursor-move mt-1 mr-2 text-gray-400 hover:text-gray-600"
        >
          <GripVertical className="w-4 h-4" />
        </div>

        <div className="flex-1">
          {isEditing ? (
            <div>
              <textarea
                ref={textareaRef}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                className="w-full p-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                rows={4}
                placeholder="Escribe una regla... Usa @NombreCategoria para referenciar categorías"
                autoFocus
              />
              <div className="flex justify-end gap-2 mt-2">
                <button
                  onClick={() => {
                    setEditText(rule.text);
                    setIsEditing(false);
                  }}
                  className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  className="px-3 py-1 text-sm text-white bg-blue-500 rounded hover:bg-blue-600"
                >
                  Guardar
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div 
                className={`text-gray-700 ${!isExpanded ? 'line-clamp-2' : ''} cursor-pointer`}
                onClick={() => setIsEditing(true)}
              >
                {renderTextWithMentions(rule.text)}
              </div>
              {rule.text.length > 200 && (
                <button
                  onClick={onToggleExpand}
                  className="text-xs text-blue-600 hover:text-blue-700 mt-1 flex items-center gap-1"
                >
                  {isExpanded ? (
                    <>
                      <ChevronUp className="w-3 h-3" />
                      Ver menos
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-3 h-3" />
                      Ver más
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>

        <button
          onClick={onDelete}
          className="ml-2 text-red-500 hover:text-red-700"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const GeneralRulesModal: React.FC<GeneralRulesModalProps> = ({
  isOpen,
  onClose,
  clientId,
  categories
}) => {
  const [rules, setRules] = useState<Rule[]>([]);
  const [newRuleText, setNewRuleText] = useState('');
  const [showNewRuleInput, setShowNewRuleInput] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Cargar reglas desde Firestore
  useEffect(() => {
    if (!isOpen) return;

    const loadRules = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, 'clients', clientId, 'catalogRules', 'general');
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          setRules(data.rules || []);
        }
      } catch (error) {
        console.error('Error loading rules:', error);
      } finally {
        setLoading(false);
      }
    };

    loadRules();
  }, [isOpen, clientId]);

  // Guardar reglas en Firestore
  const saveRules = async () => {
    setSaving(true);
    try {
      const docRef = doc(db, 'clients', clientId, 'catalogRules', 'general');
      await setDoc(docRef, {
        rules,
        updatedAt: Timestamp.now()
      });
      
      onClose();
    } catch (error) {
      console.error('Error saving rules:', error);
      alert('Error al guardar las reglas');
    } finally {
      setSaving(false);
    }
  };

  // Agregar nueva regla
  const addRule = () => {
    if (!newRuleText.trim()) return;

    const newRule: Rule = {
      id: `rule-${Date.now()}`,
      text: newRuleText.trim(),
      order: rules.length,
      createdAt: Timestamp.now()
    };

    setRules([...rules, newRule]);
    setNewRuleText('');
    setShowNewRuleInput(false);
  };

  // Actualizar regla
  const updateRule = (id: string, text: string) => {
    setRules(rules.map(rule => 
      rule.id === id ? { ...rule, text } : rule
    ));
  };

  // Eliminar regla
  const deleteRule = (id: string) => {
    if (window.confirm('¿Eliminar esta regla?')) {
      setRules(rules.filter(rule => rule.id !== id));
    }
  };

  // Toggle expandir regla
  const toggleRuleExpand = (id: string) => {
    setRules(rules.map(rule => 
      rule.id === id ? { ...rule, expanded: !rule.expanded } : rule
    ));
  };

  // Manejar drag & drop
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = rules.findIndex(r => r.id === active.id);
      const newIndex = rules.findIndex(r => r.id === over.id);
      
      const newRules = [...rules];
      const [movedRule] = newRules.splice(oldIndex, 1);
      newRules.splice(newIndex, 0, movedRule);
      
      // Actualizar orden
      newRules.forEach((rule, index) => {
        rule.order = index;
      });
      
      setRules(newRules);
    }
  };

  // Manejar input con @menciones
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    setNewRuleText(text);
    
    // Detectar si se está escribiendo una @mención
    const cursorPos = e.target.selectionStart;
    setCursorPosition(cursorPos);
    
    const textBeforeCursor = text.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1 && lastAtIndex === textBeforeCursor.length - 1) {
      setShowMentionSuggestions(true);
      setMentionSearch('');
    } else if (lastAtIndex !== -1 && textBeforeCursor.substring(lastAtIndex).match(/^@\w*$/)) {
      const search = textBeforeCursor.substring(lastAtIndex + 1);
      setMentionSearch(search);
      setShowMentionSuggestions(true);
    } else {
      setShowMentionSuggestions(false);
    }
  };

  // Insertar mención
  const insertMention = (categoryName: string) => {
    const textBeforeCursor = newRuleText.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    const textAfterCursor = newRuleText.substring(cursorPosition);
    
    const newText = 
      newRuleText.substring(0, lastAtIndex) + 
      `@${categoryName} ` + 
      textAfterCursor;
    
    setNewRuleText(newText);
    setShowMentionSuggestions(false);
    
    // Focus back to input
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        const newCursorPos = lastAtIndex + categoryName.length + 2;
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const filteredCategories = categories.filter(cat =>
    cat.name.toLowerCase().includes(mentionSearch.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        <div className="inline-block w-full max-w-4xl px-4 pt-5 pb-4 overflow-visible text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                Reglas de Catalogación
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Reglas generales para todas las categorías
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {loading ? (
            <div className="py-12 text-center">
              <Loader className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">Cargando reglas...</p>
            </div>
          ) : (
            <>
              {/* Lista de reglas */}
              <div className="space-y-3 max-h-[400px] overflow-y-auto relative">
                {rules.length > 0 ? (
                  <DndContext
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={rules.map(r => r.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {rules.map((rule) => (
                        <SortableRule
                          key={rule.id}
                          rule={rule}
                          onUpdate={(text) => updateRule(rule.id, text)}
                          onDelete={() => deleteRule(rule.id)}
                          onToggleExpand={() => toggleRuleExpand(rule.id)}
                          categories={categories}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No hay reglas definidas</p>
                  </div>
                )}
              </div>

              {/* Input para nueva regla */}
              {showNewRuleInput ? (
                <div className="mt-4 relative">
                  <textarea
                    ref={inputRef}
                    value={newRuleText}
                    onChange={handleInputChange}
                    placeholder="Escribe una nueva regla... Usa @NombreCategoria para referenciar categorías"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    rows={3}
                  />
                  
                  {/* Sugerencias de @menciones */}
                  {showMentionSuggestions && filteredCategories.length > 0 && (
                    <div 
                      className="absolute z-[9999] w-64 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl"
                      style={{ 
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        maxHeight: '200px',
                        overflowY: 'auto'
                      }}
                    >
                      <div className="py-1">
                        {filteredCategories.map(cat => (
                          <button
                            key={cat.id}
                            onClick={() => insertMention(cat.name)}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
                          >
                            <span className="font-medium">@{cat.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-end gap-2 mt-2">
                    <button
                      onClick={() => {
                        setShowNewRuleInput(false);
                        setNewRuleText('');
                      }}
                      className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={addRule}
                      className="px-3 py-1.5 text-sm text-white bg-green-600 rounded hover:bg-green-700"
                      disabled={!newRuleText.trim()}
                    >
                      Agregar Regla
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowNewRuleInput(true)}
                  className="mt-4 flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                >
                  <Plus className="w-4 h-4" />
                  Nueva Regla
                </button>
              )}

              {/* Footer */}
              <div className="mt-6 flex justify-between items-center">
                <div className="text-sm text-gray-500">
                  {rules.length} {rules.length === 1 ? 'regla' : 'reglas'}
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                    disabled={saving}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={saveRules}
                    className="flex items-center gap-2 px-4 py-2 text-white bg-blue-500 rounded-lg hover:bg-blue-600"
                    disabled={saving}
                  >
                    {saving ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    Guardar Reglas
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GeneralRulesModal;